import { useEffect, useId, useRef, useState, type MouseEvent } from 'react'
import { useRouter } from 'next/router'
import SearchOutlined from '@mui/icons-material/SearchOutlined'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ExpandMore from '@mui/icons-material/ExpandMore'
import HomeOutlined from '@mui/icons-material/HomeOutlined'
import PersonOutline from '@mui/icons-material/PersonOutline'
import FavoriteBorder from '@mui/icons-material/FavoriteBorder'
import Check from '@mui/icons-material/Check'
import Tune from '@mui/icons-material/Tune'
import Badge from '@mui/material/Badge'
import Link from 'src/components/Link'
import { useDispatch, useSelector } from 'src/redux/hooks'
import shopInfos from 'template/infos'
import { hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'
import { getNavigationImageProps } from './navigationImages'
import type { item, tMenu } from 'src/shared/frontendTypes/navigation'
import NavigationRedux from './Navigation.Redux'
import DrawerRedux from './Drawer.Redux'
import ModernFilterPanel from './ModernFilterPanel'
import styles from './ModernNavigation.module.css'

interface Props {
  paths: tMenu
  activeMenuId: number | null
  activeBrandId: number | null
}

const badgeColor = String(shopInfos.host) === 'www.homeylove.de' ? 'primary' : 'secondary'

const ModernNavigation = ({ paths, activeMenuId, activeBrandId }: Props) => {
  const router = useRouter()
  const dispatch = useDispatch()
  // Like the header, these start with the server state and restore after mount.
  const loggedIn = useSelector(store => Boolean(store.User.token))
  const wishlistCount = useSelector(store => Object.keys(store.Wunschliste.items).length)
  const { toggle: closeDrawer } = DrawerRedux()
  const { displayFilter, setDisplayFilter, resetFilter } = NavigationRedux()
  // One open branch per level keeps even a deep catalog easy to scan.
  const [expanded, setExpanded] = useState<number[]>([])
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const previouslyShowingFilter = useRef(displayFilter)
  const menuId = useId()
  const canFilter = router.pathname === '/suche' || /^\/(m|b)\//.test(router.pathname)

  useEffect(() => {
    if (previouslyShowingFilter.current && !displayFilter) {
      filterButtonRef.current?.focus({ preventScroll: true })
    }
    previouslyShowingFilter.current = displayFilter
  }, [displayFilter])

  const navigate = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
    if (router.asPath.split(/[?#]/)[0] !== href.split(/[?#]/)[0]) resetFilter()
    setDisplayFilter(false)
    closeDrawer(event)
  }

  const renderItems = (items: tMenu, depth = 0, parentBrand: item | null = null) => (
    <ul className={depth === 0 ? styles.categories : styles.children}>
      {items.map((entry, index) => {
        const href = hrefForNavigationItem(entry, parentBrand)
        const hasChildren = Boolean(entry.children?.length)
        const open = expanded[depth] === entry.id
        const panelId = `${menuId}-category-${entry.id}`
        const selected = Boolean(
          (activeMenuId && entry.menueId === activeMenuId
            && (!activeBrandId || parentBrand?.brand === activeBrandId))
          || (!activeMenuId && activeBrandId && entry.brand === activeBrandId)
        )
        const rowClass = depth === 0 ? styles.card : styles.subcategory
        const label = <>
          {depth === 0 && entry.imageSrc && <img
            {...getNavigationImageProps(entry.imageSrc)}
            className={styles.image} alt='' loading={index < 4 ? 'eager' : 'lazy'} decoding='async'
            onError={event => {
              const image = event.currentTarget
              if (image.getAttribute('src') !== entry.imageSrc) {
                image.removeAttribute('srcset')
                image.src = entry.imageSrc || ''
              } else image.style.visibility = 'hidden'
            }}
          />}
          <span className={styles.title}>{entry.title}</span>
          {hasChildren ? <ExpandMore className={`${styles.arrow} ${open ? styles.expanded : ''}`} />
            : <ChevronRight className={styles.arrow} />}
        </>

        return <li key={entry.id} className={depth === 0 ? styles.category : undefined}>
          {hasChildren ? <button
            type='button' className={rowClass} aria-expanded={open} aria-controls={panelId}
            onClick={() => setExpanded(current => open
              ? current.slice(0, depth)
              : [...current.slice(0, depth), entry.id])}
          >{label}</button> : href ? <Link
            href={href} className={rowClass} aria-current={selected ? 'page' : undefined}
            onClick={event => navigate(event, href)}
          >{label}</Link> : <span className={rowClass}>{label}</span>}

          {hasChildren && <div id={panelId} hidden={!open}>
            {open && <>
              {href && <Link href={href} className={styles.viewAll}
                aria-current={selected ? 'page' : undefined}
                onClick={event => navigate(event, href)}
              >{entry.title === 'Marken' ? 'Alle Marken entdecken' : `Alles in ${entry.title}`}<ChevronRight /></Link>}
              {renderItems(entry.children || [], depth + 1, entry.brand ? entry : parentBrand)}
            </>}
          </div>}
        </li>
      })}
    </ul>
  )

  if (displayFilter && canFilter) return <ModernFilterPanel />

  return <div className={styles.root} data-mobile-menu='modern'>
    <div className={styles.tools}>
      <button type='button' className={styles.search} onClick={event => {
        closeDrawer(event)
        dispatch({ type: 'mobileSucheToggle', payload: true })
      }}><SearchOutlined /><span>Wonach suchst du?</span></button>
      {canFilter && <button ref={filterButtonRef} type='button' className={styles.filter} onClick={() => setDisplayFilter(true)}>
        <Tune /><span>Diese Auswahl filtern</span><ChevronRight />
      </button>}
    </div>
    <nav className={styles.scroll} aria-label='Kategorien'>
      {renderItems(paths)}
    </nav>
    <nav className={styles.shortcuts} aria-label='Persönlicher Bereich'>
      <Link href='/' onClick={closeDrawer}>
        <HomeOutlined /><span>Home</span>
      </Link>
      <Link href='/kundenkonto' onClick={closeDrawer}
        aria-label={loggedIn ? 'Kundenkonto – angemeldet' : 'Kundenkonto'}>
        {loggedIn ? <Badge color={badgeColor} badgeContent={<Check className={styles.accountCheck} />} aria-hidden='true'>
          <PersonOutline />
        </Badge> : <PersonOutline />}
        <span className={styles.shortcutLabel}>Kundenkonto
          {loggedIn && <span className={styles.accountStatus} aria-hidden='true'>Angemeldet</span>}
        </span>
      </Link>
      <Link href='/wunschliste' onClick={closeDrawer}
        aria-label={wishlistCount ? `Wunschliste, ${wishlistCount} Artikel` : 'Wunschliste'}>
        {wishlistCount > 0 ? <Badge badgeContent={wishlistCount} color={badgeColor} aria-hidden='true'>
          <FavoriteBorder />
        </Badge> : <FavoriteBorder />}
        <span>Wunschliste</span>
      </Link>
    </nav>
  </div>
}

export default ModernNavigation
